// src/auth/error-codes.ts
var AuthErrorCode = {
  USER_ARCHIVED: "USER_ARCHIVED",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  INVALID_REFRESH_TOKEN: "INVALID_REFRESH_TOKEN",
  SESSION_REVOKED: "SESSION_REVOKED"
};

// src/user/user.ts
import * as zod from "zod";
var z2 = zod.z ?? zod;
var UserRoleSchema = z2.enum(["WORKER", "MANAGER", "DIRECTOR", "SUPERUSER"]);
var Role = UserRoleSchema.enum;
var UserRolesSchema = z2.array(UserRoleSchema).nonempty("Mindestens eine Rolle ausw\xE4hlen");
var ROLE_RANK = {
  WORKER: 0,
  MANAGER: 1,
  DIRECTOR: 2,
  SUPERUSER: 3
};
var hasRole = (roles, r) => roles.includes(r);
var hasAnyRole = (roles, rs) => roles.some((r) => rs.includes(r));
var hasOnlyRole = (roles, r) => roles.length === 1 && roles[0] === r;
var highestRole = (roles) => roles.reduce((acc, r) => ROLE_RANK[r] > ROLE_RANK[acc] ? r : acc, "WORKER");
var TechnicianProfileDataSchema = z2.object({
  workingDays: z2.array(z2.number().int().min(1).max(7)),
  shiftStart: z2.string(),
  shiftEnd: z2.string(),
  breakStart: z2.string(),
  trades: z2.array(z2.number().int().nonnegative())
});
var CreateUserDtoSchema = z2.object({
  email: z2.string().email(),
  name: z2.string(),
  roles: UserRolesSchema,
  technicianProfile: TechnicianProfileDataSchema.optional(),
  password: z2.string().min(8).optional()
}).superRefine((d, ctx) => {
  if (d.roles.includes("WORKER") && !d.technicianProfile) {
    ctx.addIssue({ code: z2.ZodIssueCode.custom, message: "Techniker-Profil ist erforderlich f\xFCr Handwerker", path: ["technicianProfile"] });
  }
  if (!d.roles.includes("WORKER") && d.technicianProfile) {
    ctx.addIssue({ code: z2.ZodIssueCode.custom, message: "Techniker-Profil nur f\xFCr Handwerker erlaubt", path: ["technicianProfile"] });
  }
});
var UpdateUserDtoSchema = z2.object({
  email: z2.string().email().optional(),
  name: z2.string().optional(),
  roles: UserRolesSchema.optional(),
  technicianProfile: TechnicianProfileDataSchema.optional()
});
var UserStatusFilterSchema = z2.enum(["active", "archived", "all"]);
var UserFiltersSchema = z2.preprocess(
  (raw) => {
    if (raw !== null && typeof raw === "object" && !Array.isArray(raw)) {
      const { role, roles, ...rest } = raw;
      const normalizedRoles = roles !== void 0 ? Array.isArray(roles) ? roles : [roles] : role !== void 0 ? [role] : void 0;
      return normalizedRoles !== void 0 ? { ...rest, roles: normalizedRoles } : rest;
    }
    return raw;
  },
  z2.object({
    page: z2.coerce.number().int().positive().optional(),
    limit: z2.coerce.number().int().positive().max(500).optional(),
    search: z2.string().optional(),
    roles: z2.array(UserRoleSchema).optional(),
    status: UserStatusFilterSchema.optional(),
    tradeId: z2.coerce.number().int().nonnegative().optional()
  })
);

// src/audit-log/audit-log.ts
import * as zod2 from "zod";
var z4 = zod2.z ?? zod2;
var AuditLogEntityTypeSchema = z4.enum(["WORK_ORDER", "APPOINTMENT", "TASK"]);
var AuditLogActionSchema = z4.enum([
  "WORK_ORDER_CREATED",
  "WORK_ORDER_STATUS_CHANGED",
  "WORK_ORDER_COMPLETED",
  "WORK_ORDER_CONTACT_ATTEMPT_ADDED",
  "WORK_ORDER_CONTACT_ATTEMPT_DELETED",
  "WORK_ORDER_CONTACT_COMMENT_UPDATED",
  "WORK_ORDER_NOT_REACHED_REMINDER_SENT",
  "APPOINTMENT_MATERIAL_REMINDER_SENT",
  "APPOINTMENT_CREATED",
  "APPOINTMENT_UPDATED",
  "APPOINTMENT_RESCHEDULED",
  "APPOINTMENT_TECHNICIAN_CHANGED",
  "APPOINTMENT_TRAVEL_STARTED",
  "APPOINTMENT_STARTED",
  "APPOINTMENT_COMPLETED",
  "APPOINTMENT_DEPARTURE_COMPLETED",
  "APPOINTMENT_TIMES_CORRECTED",
  "APPOINTMENT_PHASE_REVERTED",
  "APPOINTMENT_FOLLOW_UP_REQUESTED",
  "APPOINTMENT_EXPIRED",
  "APPOINTMENT_NOT_CLOSED",
  "APPOINTMENT_DELETED",
  "APPOINTMENT_SERIES_UPDATED",
  "APPOINTMENT_SERIES_EXTENDED",
  "APPOINTMENT_SERIES_SHORTENED",
  "APPOINTMENT_DETACHED_FROM_SERIES",
  "TASK_TRAVEL_STARTED",
  "TASK_STARTED",
  "TASK_COMPLETED",
  "TASK_DEPARTURE_COMPLETED",
  "TASK_TIMES_CORRECTED",
  "TASK_PHASE_REVERTED",
  "TASK_RESCHEDULED",
  "TASK_NOT_CLOSED",
  "TASK_UPDATED",
  "TASK_DELETED",
  "TASK_SERIES_EXTENDED",
  "TASK_SERIES_SHORTENED",
  "TASK_DETACHED_FROM_SERIES"
]);
var AuditLogActorSchema = z4.object({
  id: z4.string(),
  name: z4.string(),
  roles: z4.array(UserRoleSchema)
}).nullable();
var AuditLogSchema = z4.object({
  id: z4.number().int(),
  entityType: AuditLogEntityTypeSchema,
  entityId: z4.string(),
  workOrderId: z4.string().nullable(),
  action: z4.string(),
  description: z4.string(),
  payload: z4.unknown().nullable().optional(),
  actorUserId: z4.number().int().nullable(),
  actorRoles: z4.array(UserRoleSchema),
  actor: AuditLogActorSchema,
  createdAt: z4.string()
});
var AuditLogFiltersSchema = z4.object({
  workOrderId: z4.string()
});

// src/work-order/work-order.ts
import * as zod3 from "zod";
var z6 = zod3.z ?? zod3;
var WorkOrderMieterSchema = z6.object({
  name: z6.string().nullable(),
  telefon: z6.string().nullable(),
  email: z6.string().nullable()
});
var ImmotionAttachmentSchema = z6.object({
  filename: z6.string(),
  mime: z6.string(),
  base64: z6.string()
});
var WorkOrderExternalSnapshotSchema = z6.object({
  auftragJahr: z6.number().int().positive(),
  auftragNr: z6.number().int().positive(),
  auftragsdatum: z6.string().nullable(),
  ausfuehrungBis: z6.string().nullable(),
  fertigmeldungsdat: z6.string().nullable(),
  auftStatus: z6.number().int().nullable(),
  ve: z6.number().int().nullable(),
  wohnung: z6.number().int().nullable(),
  veAdresse: z6.string().nullable(),
  hauseingang: z6.string().nullable(),
  gebaeude: z6.string().nullable(),
  mieter: WorkOrderMieterSchema,
  handwerkerName: z6.string().nullable(),
  gewerkId: z6.number().int().nullable(),
  gewerkeText: z6.string().nullable(),
  leistungText: z6.string().nullable(),
  infoText: z6.string().nullable(),
  individuellerText: z6.string().nullable(),
  erfasserName: z6.string().nullable(),
  isWohnungsmodernisierung: z6.boolean(),
  immotionAttachments: z6.array(ImmotionAttachmentSchema).default([]),
  immotionAttachmentsCount: z6.number().int().nonnegative().default(0)
});
var WorkOrderLocalDataSchema = z6.object({
  updatedAt: z6.string().nullable()
});
var WorkOrderStatusSchema = z6.enum([
  "NOT_PLANNED",
  "PLANNED",
  "FOLLOW_UP_REQUIRED_1",
  "FOLLOW_UP_PLANNED_1",
  "FOLLOW_UP_REQUIRED_2",
  "FOLLOW_UP_PLANNED_2",
  "COMPLETED",
  "COMPLETED_FOLLOW_UP_1",
  "COMPLETED_FOLLOW_UP_2",
  // The tenant could not be reached. Up to three attempts are recorded; after that the office may
  // set CANCELLED.
  "NOT_REACHED",
  // Shown as „Gelöscht". A soft status: the Auftrag, its attempts and its audit trail stay.
  "CANCELLED"
]);
var WorkOrderSchema = z6.object({
  workOrderId: z6.string().nullable(),
  status: WorkOrderStatusSchema,
  external: WorkOrderExternalSnapshotSchema,
  /** Recorded contact attempts — the list's „2/3" chip. The attempts themselves are on the detail. */
  contactAttemptCount: z6.number().int().min(0).default(0)
});
var WorkOrderSortFieldSchema = z6.enum([
  "workOrderId",
  "mieter",
  "handwerker",
  "datum",
  "ve",
  "hauseingang",
  "wohnung"
]);
var WorkOrderSortDirectionSchema = z6.enum(["ASC", "DESC"]);
var WorkOrderFiltersSchema = z6.object({
  page: z6.coerce.number().int().positive().optional(),
  limit: z6.coerce.number().int().positive().max(500).optional(),
  workOrderId: z6.string().optional(),
  mieter: z6.string().optional(),
  handwerker: z6.string().optional(),
  datum: z6.string().optional(),
  datumFrom: z6.string().optional(),
  datumTo: z6.string().optional(),
  wohnung: z6.coerce.number().int().optional(),
  hauseingang: z6.string().optional(),
  ve: z6.coerce.number().int().optional(),
  veAdresse: z6.string().optional(),
  sortField: WorkOrderSortFieldSchema.optional(),
  sortDirection: WorkOrderSortDirectionSchema.optional(),
  local: z6.preprocess((v) => v === "false" ? false : v === "true" ? true : v, z6.boolean().optional()),
  status: WorkOrderStatusSchema.optional(),
  gewerkId: z6.coerce.number().int().optional()
});
var CreateWorkOrderDtoSchema = z6.object({
  workOrderId: z6.string()
});
var UpdateWorkOrderStatusDtoSchema = z6.object({
  status: WorkOrderStatusSchema
});

// src/work-order/work-order-detail.ts
import { z as z14 } from "zod";

// src/appointment/appointment.ts
import { z as z11 } from "zod";

// src/schedule/schedule-entry.ts
import { z as z8 } from "zod";

// src/absence/absence.ts
import { z as z7 } from "zod";
var AbsenceCategorySchema = z7.enum([
  "SICK_LEAVE",
  "VACATION",
  "TRAINING",
  "PERSONAL_LEAVE"
]);
var AbsenceTechnicianSchema = z7.object({
  id: z7.number().int(),
  fullName: z7.string()
});
var AbsenceSchema = z7.object({
  id: z7.number().int(),
  createdByUserId: z7.number().int(),
  /** NOT NULL — with `entryType` gone, every absence has a category. */
  category: AbsenceCategorySchema,
  comment: z7.string().nullable().optional(),
  /** Author intent. Per-technician occupancy lives on `entries`. */
  startsAt: z7.string().datetime(),
  endsAt: z7.string().datetime(),
  technicians: z7.array(AbsenceTechnicianSchema),
  createdAt: z7.string().datetime()
});
var CreateAbsenceDtoSchema = z7.object({
  technicianIds: z7.array(z7.coerce.number().int().positive()).min(1),
  category: AbsenceCategorySchema,
  comment: z7.string().nullable().optional(),
  startsAt: z7.string().datetime(),
  endsAt: z7.string().datetime(),
  /**
   * Book anyway and flag every displaced appointment/task `needsRescheduling`. An absence never
   * yields to another absence it overlaps — force books both, flags neither.
   */
  force: z7.boolean().optional()
});
var UpdateAbsenceDtoSchema = z7.object({
  technicianIds: z7.array(z7.coerce.number().int().positive()).min(1).optional(),
  category: AbsenceCategorySchema.optional(),
  comment: z7.string().nullable().optional(),
  startsAt: z7.string().datetime().optional(),
  endsAt: z7.string().datetime().optional(),
  force: z7.boolean().optional()
});
var AbsenceSortFieldSchema = z7.enum(["category", "startsAt", "endsAt"]);
var AbsenceSortDirectionSchema = z7.enum(["ASC", "DESC"]);
var AbsenceFiltersSchema = z7.object({
  page: z7.coerce.number().int().positive().optional(),
  limit: z7.coerce.number().int().positive().max(500).optional(),
  technicianName: z7.string().optional(),
  category: AbsenceCategorySchema.optional(),
  dateFrom: z7.string().optional(),
  dateTo: z7.string().optional(),
  sortField: AbsenceSortFieldSchema.optional(),
  sortDirection: AbsenceSortDirectionSchema.optional()
});
var AbsenceListResultSchema = z7.object({
  items: z7.array(AbsenceSchema),
  meta: z7.object({
    page: z7.number().int(),
    limit: z7.number().int(),
    total: z7.number().int()
  })
});

// src/schedule/schedule-entry.ts
var ScheduleEntryTypeSchema = z8.enum([
  "APPOINTMENT",
  "TASK",
  "ABSENCE",
  "BREAK"
]);
var RescheduleCauseSchema = z8.enum([
  /** Displaced by an absence, or by any block force-planned on top of it. */
  "ABSENCE",
  /** Force-planned onto a day the technician does not work ("Trotzdem planen"). */
  "OUTSIDE_WORKING_HOURS",
  /** Flagged wholesale when the technician was archived. */
  "TECHNICIAN_ARCHIVED",
  /** Still open at 20:00 on its planned day — never started, or started and not closed. */
  "NOT_CLOSED"
]);
var SegmentedSlotSchema = z8.object({
  kind: z8.literal("SEGMENTED"),
  /** Work start. The block starts `travelMinutes` earlier. */
  workStartsAt: z8.string().datetime(),
  travelMinutes: z8.number().int().min(0).default(0),
  workMinutes: z8.number().int().min(0),
  departureMinutes: z8.number().int().min(0).default(0)
});
var SpanSlotSchema = z8.object({
  kind: z8.literal("SPAN"),
  startsAt: z8.string().datetime(),
  endsAt: z8.string().datetime()
});
var SlotSchema = z8.discriminatedUnion("kind", [SegmentedSlotSchema, SpanSlotSchema]).superRefine((slot, ctx) => {
  if (slot.kind === "SEGMENTED") {
    if (slot.travelMinutes + slot.workMinutes + slot.departureMinutes <= 0) {
      ctx.addIssue({
        code: z8.ZodIssueCode.custom,
        message: "Slot must occupy a positive amount of time",
        path: ["workMinutes"]
      });
    }
    return;
  }
  if (new Date(slot.endsAt).getTime() <= new Date(slot.startsAt).getTime()) {
    ctx.addIssue({
      code: z8.ZodIssueCode.custom,
      message: "endsAt must be after startsAt",
      path: ["endsAt"]
    });
  }
});
var ScheduleEntryTechnicianSchema = z8.object({
  id: z8.number().int(),
  fullName: z8.string().nullable(),
  avatar: z8.string().nullable().optional()
});
var ScheduleEntrySchema = z8.object({
  /** `null` marks a default break projected from the technician profile — it has no row. */
  id: z8.number().int().nullable(),
  type: ScheduleEntryTypeSchema,
  technicianId: z8.number().int(),
  createdByUserId: z8.number().int().nullable(),
  /** Derived occupancy, travel included. CHECK-enforced, never client-supplied. */
  startsAt: z8.string().datetime(),
  endsAt: z8.string().datetime(),
  plannedWorkStartsAt: z8.string().datetime(),
  plannedTravelMinutes: z8.number().int().min(0),
  plannedWorkMinutes: z8.number().int().min(0),
  plannedDepartureMinutes: z8.number().int().min(0),
  actualWorkStartsAt: z8.string().datetime().nullable(),
  /**
   * When the work ended — stamped by the worker's sign-off tap, or set by the office. NOT the
   * occupancy end: `endsAt` is computed from the minutes. The minutes are themselves derived from
   * the four instants, so the two agree up to the one-minute work floor; do not "fix" either.
   */
  actualEndsAt: z8.string().datetime().nullable(),
  /**
   * The outer two of the four lifecycle instants, stamped by "Anfahrt starten" / "Abfahrt beenden"
   * or set by the office. The three billable minutes below are **derived** from the four instants
   * by the server (`deriveActualMinutes`) — nothing writes them directly — and the runtime phase
   * derives from the same instants via `deriveWorkPhase` (work-item). Always null for ABSENCE/BREAK.
   */
  travelStartedAt: z8.string().datetime().nullable(),
  departureEndedAt: z8.string().datetime().nullable(),
  actualTravelMinutes: z8.number().int().min(0).nullable(),
  actualWorkMinutes: z8.number().int().min(0).nullable(),
  actualDepartureMinutes: z8.number().int().min(0).nullable(),
  allDay: z8.boolean(),
  needsRescheduling: z8.boolean(),
  reschedulingCause: RescheduleCauseSchema.nullable(),
  /** Only meaningful for cause `ABSENCE` — the category of the absence that displaced this block. */
  reschedulingReason: AbsenceCategorySchema.nullable(),
  reschedulingRequestedAt: z8.string().datetime().nullable(),
  appointmentId: z8.number().int().nullable(),
  taskId: z8.number().int().nullable(),
  absenceId: z8.number().int().nullable(),
  technician: ScheduleEntryTechnicianSchema.nullable().optional(),
  /** Human-readable owner label for calendar rendering. */
  label: z8.string().nullable().optional(),
  /**
   * Present on ABSENCE entries: the parent's category, denormalised so a calendar can colour
   * and label the block without a second request for the absence itself.
   */
  category: AbsenceCategorySchema.nullable().optional(),
  createdAt: z8.string().datetime().nullable(),
  updatedAt: z8.string().datetime().nullable()
});
var ScheduleConflictItemSchema = z8.object({
  /** `null` for a default break projected from the technician profile. */
  entryId: z8.number().int().nullable(),
  type: ScheduleEntryTypeSchema,
  technicianId: z8.number().int(),
  technicianName: z8.string(),
  startsAt: z8.string().datetime(),
  endsAt: z8.string().datetime(),
  label: z8.string(),
  appointmentId: z8.number().int().nullable().optional(),
  taskId: z8.number().int().nullable().optional(),
  absenceId: z8.number().int().nullable().optional(),
  /** Only set for type ABSENCE — the category that wins the overlap and never yields it. */
  absenceCategory: AbsenceCategorySchema.nullable().optional()
});
var ScheduleWorkingHoursViolationReasonSchema = z8.enum([
  /** The block touches a calendar day absent from `TechnicianProfile.workingDays`. */
  "NON_WORKING_DAY",
  /** The block falls outside `[shiftStart, shiftEnd)` for its day. */
  "OUTSIDE_SHIFT"
]);
var ScheduleWorkingHoursViolationSchema = z8.object({
  reason: ScheduleWorkingHoursViolationReasonSchema,
  technicianId: z8.number().int(),
  technicianName: z8.string(),
  /** The offending LOCAL calendar day as `YYYY-MM-DD` in Europe/Berlin. Never an instant. */
  date: z8.string(),
  /** The planned block that was rejected, so the client can render what it asked for. */
  startsAt: z8.string().datetime(),
  endsAt: z8.string().datetime(),
  /** The shift window for `date`. Null for NON_WORKING_DAY — there is no shift that day. */
  shiftStartsAt: z8.string().datetime().nullable(),
  shiftEndsAt: z8.string().datetime().nullable()
});

// src/schedule/schedule-series.ts
import { z as z9 } from "zod";
var RecurrenceFrequencySchema = z9.enum(["WEEKLY", "BIWEEKLY", "MONTHLY"]);
var ScheduleSeriesTypeSchema = z9.enum(["APPOINTMENT", "TASK"]);
var RecurrenceInputSchema = z9.object({
  frequency: RecurrenceFrequencySchema,
  until: z9.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format YYYY-MM-DD")
});
var ScheduleSeriesSchema = z9.object({
  id: z9.string().uuid(),
  type: ScheduleSeriesTypeSchema,
  frequency: RecurrenceFrequencySchema,
  /**
   * Work start of the first occurrence, and the anchor every later one is derived from. For
   * MONTHLY this is what carries the original day-of-month, so a series starting on the 31st
   * yields 28 Feb and then 31 Mar rather than drifting to 28 Mar.
   */
  startsAt: z9.string().datetime(),
  /** Horizon — occurrences exist up to and including the end of this local day. */
  endsAt: z9.string().datetime(),
  createdByUserId: z9.number().int(),
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var UpdateScheduleSeriesDtoSchema = z9.object({
  until: z9.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format YYYY-MM-DD"),
  /**
   * Generate occurrences even on days the technician does not work. Extending is all-or-nothing,
   * so a MONTHLY series growing into a Saturday is otherwise rejected in full.
   */
  allowOutsideWorkingHours: z9.boolean().optional()
});

// src/work-item/work-item.ts
import { z as z10 } from "zod";
var WorkPhaseSchema = z10.enum(["ANFAHRT", "ARBEIT", "ABFAHRT", "DONE"]);
function deriveWorkPhase(entry) {
  if (entry.actualEndsAt != null) {
    if (entry.plannedDepartureMinutes > 0 && entry.departureEndedAt == null) {
      return "ABFAHRT";
    }
    return "DONE";
  }
  if (entry.actualWorkStartsAt != null) return "ARBEIT";
  if (entry.travelStartedAt != null) return "ANFAHRT";
  return null;
}
var isSessionOpen = (phase) => phase === "ANFAHRT" || phase === "ARBEIT" || phase === "ABFAHRT";
var CLOSED_WORK_STATUSES = [
  "COMPLETED",
  "COMPLETED_WITH_FOLLOW_UP",
  "CANCELLED",
  "EXPIRED"
];
var WORK_PHASE_RUNNING_LABELS = {
  ANFAHRT: "Anfahrt l\xE4uft",
  ARBEIT: "In Bearbeitung",
  ABFAHRT: "Abfahrt l\xE4uft"
};
function deriveWorkState(src) {
  const isClosed = CLOSED_WORK_STATUSES.includes(src.status);
  const phase = src.schedule ? deriveWorkPhase(src.schedule) : null;
  const isRunning = phase === "ABFAHRT" || (phase === "ANFAHRT" || phase === "ARBEIT") && !isClosed;
  return {
    phase,
    isClosed,
    isRunning,
    label: isRunning && phase ? WORK_PHASE_RUNNING_LABELS[phase] : null
  };
}
var CompleteWorkItemDtoSchema = z10.object({
  travelStartedAt: z10.string().datetime().optional(),
  actualWorkStartsAt: z10.string().datetime().optional(),
  actualEndsAt: z10.string().datetime().optional()
});
var TechnicianAttachmentSchema = z10.object({
  id: z10.number().int(),
  /** `/static/<uuid>.<ext>` — same static mount as the office attachments. */
  url: z10.string(),
  /** The name the file had on the phone; the stored name is a UUID. */
  originalName: z10.string(),
  mimeType: z10.string().nullable(),
  sizeBytes: z10.number().int().nullable(),
  uploadedAt: z10.string().datetime(),
  uploadedBy: z10.object({
    id: z10.number().int(),
    fullName: z10.string()
  })
});
var UpdateTechnicianCommentDtoSchema = z10.object({
  technicianComment: z10.string().max(4e3).nullable()
});
var EndDepartureDtoSchema = z10.object({
  departureEndedAt: z10.string().datetime().optional()
});
var StartWorkItemDtoSchema = z10.object({
  confirmRescheduling: z10.boolean().optional(),
  /** Why it went ahead anyway — "Mieter war da". Shown in the office push and the audit log. */
  reschedulingNote: z10.string().trim().max(200).optional()
});
var WorkTimeFieldSchema = z10.enum([
  "travelStartedAt",
  "actualWorkStartsAt",
  "actualEndsAt",
  "departureEndedAt"
]);
var WorkSessionItemTypeSchema = z10.enum(["APPOINTMENT", "TASK"]);
var CurrentWorkSessionSchema = z10.object({
  itemType: WorkSessionItemTypeSchema,
  /** Appointment id or task id, depending on `itemType`. */
  itemId: z10.number().int(),
  /** Work-order id for appointments, `null` for tasks. */
  workOrderId: z10.string().nullable(),
  /** Owner label for the banner (trade name / task title). */
  title: z10.string(),
  phase: WorkPhaseSchema,
  /** When the CURRENT phase began — the banner's timer counts from here. */
  phaseStartedAt: z10.string().datetime(),
  /** Planned minutes for the current phase; the timer turns amber beyond it. */
  plannedPhaseMinutes: z10.number().int().min(0)
});
var CurrentWorkSessionResponseSchema = z10.object({
  session: CurrentWorkSessionSchema.nullable()
});

// src/appointment/appointment.ts
var AppointmentStatusSchema = z11.enum([
  "PLANNED",
  "FOLLOW_UP",
  "COMPLETED",
  "COMPLETED_WITH_FOLLOW_UP",
  "EXPIRED"
]);
var AppointmentTechnicianSchema = z11.object({
  id: z11.number().int(),
  name: z11.string().nullable(),
  avatar: z11.string().nullable()
});
var AppointmentSchema = z11.object({
  id: z11.number().int(),
  workOrderId: z11.string(),
  sequenceNumber: z11.number().int().min(1),
  attachments: z11.array(z11.string()).nullable(),
  status: AppointmentStatusSchema,
  followUpReason: z11.string().nullable().optional(),
  description: z11.string().nullable().optional(),
  technicianComment: z11.string().nullable().optional(),
  /**
   * Files the technician sent from the field — see `TechnicianAttachmentSchema` for why these are
   * not merged into `attachments`. Always present, empty when nothing was sent.
   */
  technicianAttachments: z11.array(TechnicianAttachmentSchema),
  seriesId: z11.string().uuid().nullable().optional(),
  createdAt: z11.string(),
  updatedAt: z11.string(),
  schedule: ScheduleEntrySchema,
  technician: AppointmentTechnicianSchema.nullable(),
  workOrder: WorkOrderSchema.nullable()
});
var CreateAppointmentStatusSchema = z11.enum(["PLANNED", "FOLLOW_UP"]);
var CreateAppointmentDtoSchema = z11.object({
  workOrderId: z11.string(),
  assignedTechnicianId: z11.coerce.number().int().positive(),
  plannedStart: z11.string().datetime(),
  plannedDepartureDuration: z11.number().int().min(0),
  plannedTravelDuration: z11.number().int().min(0),
  plannedWorkDuration: z11.number().int().positive(),
  attachments: z11.array(z11.string()).nullable(),
  description: z11.string().nullable().optional(),
  isFullDay: z11.boolean().optional(),
  recurrence: RecurrenceInputSchema.optional(),
  /**
   * Omit it and the backend decides: `FOLLOW_UP` when the Auftrag is waiting for a Folgetermin
   * (`FOLLOW_UP_REQUIRED_1/2`), `PLANNED` otherwise. Send it to plan a Folgetermin in advance —
   * before the technician has asked for one. `FOLLOW_UP` needs an earlier appointment on the
   * Auftrag to follow. Applies to every occurrence of a series.
   */
  status: CreateAppointmentStatusSchema.optional(),
  /**
   * Book anyway and flag every displaced entry `needsRescheduling`. Never bypasses completed
   * work — `SCHEDULE_COMPLETED_BLOCKERS` is thrown regardless.
   */
  force: z11.boolean().optional(),
  /**
   * Plan this even though the technician does not work then — a non-working day, or outside
   * their shift. Distinct from `force`: that one overrides an *overlap* and writes (it flags
   * every entry it displaced), this one overrides a read-only rule about the technician and
   * displaces nothing. A slot can need both, either, or neither.
   *
   * With `recurrence` it applies to every occurrence: creation is all-or-nothing, so a MONTHLY
   * series that eventually lands on a Saturday needs this or the whole series is rejected.
   */
  allowOutsideWorkingHours: z11.boolean().optional()
});
var UpdateAppointmentDtoSchema = z11.object({
  assignedTechnicianId: z11.coerce.number().int().positive(),
  plannedStart: z11.string().datetime(),
  plannedDepartureDuration: z11.number().int().min(0),
  plannedTravelDuration: z11.number().int().min(0),
  plannedWorkDuration: z11.number().int().positive(),
  attachments: z11.array(z11.string()).nullable(),
  followUpReason: z11.string().nullable(),
  /**
   * The office's view of what really happened: the same four instants the worker's taps stamp
   * (see `WorkTimeFieldSchema`). The three Ist-Minuten are **derived** from these on the server,
   * never accepted. `undefined` leaves an instant alone, `null` clears it.
   *
   * A status change into `COMPLETED` / `COMPLETED_WITH_FOLLOW_UP` without an `actualEnd` makes the
   * server derive the missing instants from the plan — an office close closes everything, Abfahrt
   * included. A change back out of those statuses clears all four.
   */
  travelStartedAt: z11.string().datetime().nullable(),
  actualStart: z11.string().datetime().nullable(),
  actualEnd: z11.string().datetime().nullable(),
  departureEndedAt: z11.string().datetime().nullable(),
  description: z11.string().nullable(),
  isFullDay: z11.boolean(),
  /**
   * A manager's manual correction of the lifecycle state.
   *
   * The technician-driven path is `PATCH :id/complete` and `:id/follow-up`, which also record the
   * actuals and demand that the appointment was started. This one only moves the status — but it
   * carries the same WorkOrder side effect, so the Auftrag cannot drift out of sync with the work
   * that was actually signed off.
   *
   * There is no transition table: any status may follow any other. `EXPIRED` is normally the
   * nightly cron's to write, and setting `PLANNED` on an appointment whose start is in the past
   * will be expired again the following night — reviving an old appointment means moving its date
   * too.
   *
   * Never spread by `forFullSeries`: completing one occurrence must not complete the rest.
   */
  status: AppointmentStatusSchema,
  /**
   * Widen this edit to the whole rest of the series: the addressed appointment is the anchor,
   * and every LATER occurrence gets the same change. Completed and started ones are skipped.
   *
   * With `plannedStart` this is a **delta**, not an absolute: the anchor moves to the given time
   * and every later occurrence shifts by the same offset, so the recurrence pattern survives.
   *
   * No effect on an appointment that has no series.
   */
  forFullSeries: z11.boolean(),
  /**
   * Book anyway and flag every displaced entry `needsRescheduling`. Never bypasses completed
   * work — `SCHEDULE_COMPLETED_BLOCKERS` is thrown regardless.
   */
  force: z11.boolean(),
  /**
   * Same meaning as on create. Needed on update too: a `forFullSeries` time shift moves every
   * later occurrence by the same delta, so a +2-day shift can push a whole series onto Sundays.
   */
  allowOutsideWorkingHours: z11.boolean()
}).partial();
var DeleteAppointmentQuerySchema = z11.object({
  forFullSeries: z11.union([z11.boolean(), z11.enum(["true", "false"])]).optional().transform((v) => v === true || v === "true")
});
var DeleteAppointmentResultSchema = z11.object({
  deletedCount: z11.number().int(),
  deletedIds: z11.array(z11.number().int()),
  /** True when the last occurrence went and the series row was removed with it. */
  seriesRemoved: z11.boolean()
});
var AppointmentSeriesSummarySchema = z11.object({
  seriesId: z11.string().uuid(),
  frequency: RecurrenceFrequencySchema,
  /** Horizon of the series. Move it with PATCH /appointments/series/:seriesId. */
  until: z11.string().datetime(),
  totalCount: z11.number().int(),
  completedCount: z11.number().int(),
  firstPlannedStart: z11.string(),
  lastPlannedStart: z11.string(),
  appointments: z11.array(AppointmentSchema)
});
var UpdateAppointmentSeriesResultSchema = z11.object({
  createdCount: z11.number().int(),
  deletedCount: z11.number().int(),
  appointments: z11.array(AppointmentSchema)
});
var CompleteAppointmentDtoSchema = CompleteWorkItemDtoSchema.extend({
  technicianComment: z11.string().optional()
});
var FollowUpAppointmentDtoSchema = CompleteAppointmentDtoSchema.extend({
  followUpReason: z11.string().min(1)
});
var AppointmentSignOffResultSchema = AppointmentSchema.extend({
  followUpAlreadyPlanned: z11.boolean()
});

// src/work-order/contact-attempt.ts
import * as zod4 from "zod";
var z13 = zod4.z ?? zod4;
var MAX_CONTACT_ATTEMPTS = 3;
var NOT_REACHED_REMINDER_DAYS = 21;
var ContactAttemptSchema = z13.object({
  id: z13.number().int(),
  attemptedAt: z13.string().datetime(),
  note: z13.string(),
  createdById: z13.number().int(),
  createdByName: z13.string()
});
var CreateContactAttemptDtoSchema = z13.object({
  attemptedAt: z13.string().datetime(),
  note: z13.string().trim().max(200).default("")
});
var UpdateContactCommentDtoSchema = z13.object({
  comment: z13.string().max(2e3)
});

// src/work-order/work-order-detail.ts
var WorkOrderWithAppointmentsSchema = WorkOrderSchema.extend({
  appointments: z14.array(AppointmentSchema),
  /** Oldest first. Empty unless the Auftrag is (or was just) „Nicht erreicht". */
  contactAttempts: z14.array(ContactAttemptSchema).default([]),
  contactComment: z14.string().nullable().default(null),
  /** When the Auftrag entered „Nicht erreicht" — what „seit N Tagen" and the 3-week reminder count from. */
  notReachedSince: z14.string().datetime().nullable().default(null)
});

// src/technician-profile/technician-profile.ts
import * as zod5 from "zod";
var z16 = zod5.z ?? zod5;
var TechnicianProfileSchema = z16.object({
  id: z16.number().int(),
  userId: z16.number().int(),
  workingDays: z16.array(z16.number().int().min(1).max(7)),
  shiftStart: z16.string().datetime(),
  shiftEnd: z16.string().datetime(),
  breakStart: z16.string().datetime(),
  trades: z16.array(z16.number().int().nonnegative())
});
var CreateTechnicianProfileDtoSchema = z16.object({
  userId: z16.number().int(),
  workingDays: z16.array(z16.number().int().min(1).max(7)),
  shiftStart: z16.string().datetime(),
  shiftEnd: z16.string().datetime(),
  breakStart: z16.string().datetime(),
  trades: z16.array(z16.number().int().nonnegative())
});
var UpdateTechnicianCalendarOrderDtoSchema = z16.object({
  technicians: z16.array(
    z16.object({
      technicianId: z16.number().int().positive(),
      hidden: z16.boolean()
    })
  ).min(1).refine(
    (items) => new Set(items.map((item) => item.technicianId)).size === items.length,
    { message: "Duplicate technicianId" }
  )
});

// src/gewerk/gewerk.ts
import * as zod6 from "zod";
var z18 = zod6.z ?? zod6;
var GewerkSchema = z18.object({
  id: z18.number().int(),
  text: z18.string()
});
var GewerkFiltersSchema = z18.object({
  page: z18.coerce.number().int().min(1).optional(),
  limit: z18.coerce.number().int().min(1).max(500).optional(),
  search: z18.string().optional()
});

// src/schedule/schedule.ts
import { z as z20 } from "zod";

// src/task/task.ts
import { z as z19 } from "zod";
var TaskStatusSchema = z19.enum([
  "PLANNED",
  "COMPLETED",
  "DELAYED",
  "CANCELLED"
]);
var TaskTypeSchema = z19.enum(["ERRANDS", "GENERAL_TASK"]);
var TaskTechnicianSchema = z19.object({
  id: z19.number().int(),
  fullName: z19.string()
});
var TaskListItemSchema = z19.object({
  id: z19.number().int(),
  title: z19.string(),
  taskType: TaskTypeSchema,
  status: TaskStatusSchema,
  deadline: z19.string().datetime().nullable().optional(),
  assignedTechnician: TaskTechnicianSchema.nullable(),
  schedule: ScheduleEntrySchema.nullable(),
  seriesId: z19.string().uuid().nullable(),
  /**
   * Field feedback, in the list shape as well: the disposition board is served by this row and
   * marks a block that carries a comment or files from the technician. The files themselves stay
   * on the detail shape — the board needs to know *that* there are some, not which.
   */
  technicianComment: z19.string().nullable(),
  technicianAttachmentCount: z19.number().int().min(0)
});
var TaskSchema = z19.object({
  id: z19.number().int(),
  title: z19.string(),
  description: z19.string().nullable().optional(),
  taskType: TaskTypeSchema,
  status: TaskStatusSchema,
  deadline: z19.string().datetime().nullable().optional(),
  assignedTechnicianId: z19.number().int().nullable(),
  assignedTechnician: TaskTechnicianSchema.nullable(),
  createdByUserId: z19.number().int(),
  attachments: z19.array(z19.string()),
  /** Mirrors `Appointment.technicianComment` — the one comment from the field. */
  technicianComment: z19.string().nullable(),
  /** Mirrors `Appointment.technicianAttachments`. Always present, empty when nothing was sent. */
  technicianAttachments: z19.array(TechnicianAttachmentSchema),
  schedule: ScheduleEntrySchema.nullable(),
  seriesId: z19.string().uuid().nullable(),
  createdAt: z19.string().datetime()
});
var CreateTaskDtoSchema = z19.object({
  title: z19.string().min(1),
  description: z19.string().optional(),
  assignedTechnicianId: z19.coerce.number().int().positive().nullable().optional(),
  taskType: TaskTypeSchema,
  plannedStart: z19.string().datetime().nullable().optional(),
  deadline: z19.string().datetime().optional(),
  plannedDepartureDuration: z19.number().int().min(0),
  plannedTravelDuration: z19.number().int().min(0),
  plannedWorkDuration: z19.number().int().min(0),
  attachments: z19.array(z19.string()).optional(),
  recurrence: RecurrenceInputSchema.optional(),
  /**
   * Book anyway and flag every displaced entry `needsRescheduling`. Never bypasses completed
   * work — `SCHEDULE_COMPLETED_BLOCKERS` is thrown regardless.
   *
   * With `recurrence` it applies to every occurrence: creation is all-or-nothing, so forcing
   * half a series would be meaningless.
   */
  force: z19.boolean().optional(),
  /**
   * Plan this even though the technician does not work then — a non-working day, or outside
   * their shift. Distinct from `force`: that one overrides an *overlap* and writes (it flags
   * every entry it displaced), this one overrides a read-only rule about the technician and
   * displaces nothing. A slot can need both, either, or neither.
   */
  allowOutsideWorkingHours: z19.boolean().optional()
});
var UpdateTaskDtoSchema = z19.object({
  title: z19.string().min(1).optional(),
  description: z19.string().nullable().optional(),
  assignedTechnicianId: z19.coerce.number().int().positive().optional(),
  taskType: TaskTypeSchema.optional(),
  status: TaskStatusSchema.optional(),
  plannedStart: z19.string().datetime().optional(),
  deadline: z19.string().datetime().nullable().optional(),
  plannedDepartureDuration: z19.number().int().min(0).optional(),
  plannedTravelDuration: z19.number().int().min(0).optional(),
  plannedWorkDuration: z19.number().int().min(0).optional(),
  /**
   * The office's view of what really happened: the same four instants the worker's taps stamp
   * (see `WorkTimeFieldSchema`). The three Ist-Minuten are **derived** from these on the server,
   * never accepted — one writer, so the stored minutes can never disagree with the instants.
   * `undefined` leaves an instant alone, `null` clears it.
   *
   * Setting `status: COMPLETED` on a task that has no `actualEnd` yet makes the server derive the
   * missing instants from the plan (work start, end, and a planned Anfahrt/Abfahrt) — an office
   * close closes everything. Moving the status back out of `COMPLETED` clears all four.
   */
  travelStartedAt: z19.string().datetime().nullable().optional(),
  actualStart: z19.string().datetime().nullable().optional(),
  actualEnd: z19.string().datetime().nullable().optional(),
  departureEndedAt: z19.string().datetime().nullable().optional(),
  attachments: z19.array(z19.string()).optional(),
  /**
   * Widen this edit to the whole rest of the series: the addressed task is the anchor, and every
   * LATER occurrence gets the same change. Completed and started ones are skipped.
   *
   * With `plannedStart` this is a **delta**, not an absolute: the anchor moves to the given time
   * and every later occurrence shifts by the same offset, so the recurrence pattern survives.
   * `deadline` follows its occurrence, keeping the anchor's offset from the work start.
   *
   * `status` and the actuals are never spread — completing or starting one occurrence must not
   * touch the rest. No effect on a task that has no series.
   */
  forFullSeries: z19.boolean().optional(),
  /** Same meaning as on create. With `forFullSeries` it forces every later occurrence too. */
  force: z19.boolean().optional(),
  /**
   * Same meaning as on create. Needed on update too: a `forFullSeries` time shift moves every
   * later occurrence by the same delta, so a +2-day shift can push a whole series onto Sundays.
   */
  allowOutsideWorkingHours: z19.boolean().optional()
});
var DeleteTaskQuerySchema = z19.object({
  forFullSeries: z19.union([z19.boolean(), z19.enum(["true", "false"])]).optional().transform((v) => v === true || v === "true")
});
var DeleteTaskResultSchema = z19.object({
  deletedCount: z19.number().int(),
  deletedIds: z19.array(z19.number().int()),
  /** True when the last occurrence went and the series row was removed with it. */
  seriesRemoved: z19.boolean()
});
var TaskSeriesSummarySchema = z19.object({
  seriesId: z19.string().uuid(),
  frequency: RecurrenceFrequencySchema,
  /** Horizon of the series. Move it with PATCH /tasks/series/:seriesId. */
  until: z19.string().datetime(),
  totalCount: z19.number().int(),
  completedCount: z19.number().int(),
  firstPlannedStart: z19.string(),
  lastPlannedStart: z19.string(),
  tasks: z19.array(TaskSchema)
});
var UpdateTaskSeriesResultSchema = z19.object({
  createdCount: z19.number().int(),
  deletedCount: z19.number().int(),
  tasks: z19.array(TaskSchema)
});
var CompleteTaskDtoSchema = CompleteWorkItemDtoSchema.extend({
  technicianComment: z19.string().optional()
});
var ScheduleTaskDtoSchema = z19.object({
  plannedStart: z19.string().datetime(),
  plannedWorkDuration: z19.number().int().min(0),
  plannedTravelDuration: z19.number().int().min(0),
  plannedDepartureDuration: z19.number().int().min(0),
  /** See CreateTaskDtoSchema. Without it, dropping a task onto an occupied slot is a dead-end 409. */
  force: z19.boolean().optional(),
  /** See CreateTaskDtoSchema. Without it, dragging a task to a Saturday is a dead-end 409. */
  allowOutsideWorkingHours: z19.boolean().optional()
});
var TaskSortFieldSchema = z19.enum([
  "title",
  "plannedStart",
  "deadline",
  "taskType",
  "technicianName",
  "plannedWorkDuration",
  "plannedTravelDuration"
]);
var TaskSortDirectionSchema = z19.enum(["ASC", "DESC"]);
var TechnicianTasksQuerySchema = z19.object({
  from: z19.string(),
  to: z19.string()
});
var TechnicianTasksResponseSchema = z19.object({
  scheduledTasks: z19.array(TaskListItemSchema),
  unscheduledTasks: z19.array(TaskListItemSchema)
});
var TaskFiltersSchema = z19.object({
  page: z19.coerce.number().int().positive().optional(),
  limit: z19.coerce.number().int().positive().max(500).optional(),
  technicianName: z19.string().optional(),
  taskType: TaskTypeSchema.optional(),
  status: TaskStatusSchema.optional(),
  needsRescheduling: z19.coerce.boolean().optional(),
  title: z19.string().optional(),
  plannedStartFrom: z19.string().optional(),
  plannedStartTo: z19.string().optional(),
  deadlineFrom: z19.string().optional(),
  deadlineTo: z19.string().optional(),
  sortField: TaskSortFieldSchema.optional(),
  sortDirection: TaskSortDirectionSchema.optional()
});

// src/schedule/schedule.ts
var AppointmentScheduleDetailsSchema = z20.object({
  workOrderId: z20.string(),
  description: z20.string().nullable().optional(),
  attachments: z20.array(z20.string()).nullable().optional()
});
var TaskScheduleDetailsSchema = z20.object({
  taskId: z20.number().int().positive().optional(),
  title: z20.string().min(1).optional(),
  description: z20.string().nullable().optional(),
  taskType: TaskTypeSchema.optional(),
  deadline: z20.string().datetime().nullable().optional(),
  attachments: z20.array(z20.string()).nullable().optional()
}).refine((d) => d.taskId != null || d.title != null && d.taskType != null, {
  message: "Provide taskId to schedule an existing task, or title and taskType to create one"
});
var AbsenceScheduleDetailsSchema = z20.object({
  category: AbsenceCategorySchema,
  comment: z20.string().nullable().optional()
});
var BreakScheduleDetailsSchema = z20.object({});
var SingleTechnicianSchema = z20.array(z20.coerce.number().int().positive()).length(1);
var MultiTechnicianSchema = z20.array(z20.coerce.number().int().positive()).min(1);
var scheduleEntryCommon = {
  slot: SlotSchema,
  allDay: z20.boolean().optional(),
  force: z20.boolean().optional()
};
var AllowOutsideWorkingHoursSchema = z20.boolean().optional();
var CreateAppointmentEntryDtoSchema = z20.object({
  type: z20.literal("APPOINTMENT"),
  technicianIds: SingleTechnicianSchema,
  ...scheduleEntryCommon,
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema,
  details: AppointmentScheduleDetailsSchema
});
var CreateTaskEntryDtoSchema = z20.object({
  type: z20.literal("TASK"),
  technicianIds: SingleTechnicianSchema,
  ...scheduleEntryCommon,
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema,
  details: TaskScheduleDetailsSchema
});
var CreateAbsenceEntryDtoSchema = z20.object({
  type: z20.literal("ABSENCE"),
  technicianIds: MultiTechnicianSchema,
  ...scheduleEntryCommon,
  details: AbsenceScheduleDetailsSchema
});
var CreateBreakEntryDtoSchema = z20.object({
  type: z20.literal("BREAK"),
  technicianIds: SingleTechnicianSchema,
  ...scheduleEntryCommon,
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema,
  details: BreakScheduleDetailsSchema.optional()
});
var CreateScheduleEntryDtoSchema = z20.discriminatedUnion("type", [
  CreateAppointmentEntryDtoSchema,
  CreateTaskEntryDtoSchema,
  CreateAbsenceEntryDtoSchema,
  CreateBreakEntryDtoSchema
]);
var UpdateScheduleEntryDtoSchema = z20.object({
  technicianId: z20.coerce.number().int().positive().optional(),
  slot: SlotSchema.optional(),
  allDay: z20.boolean().optional(),
  force: z20.boolean().optional(),
  /** APPOINTMENT, TASK and BREAK entries — see AllowOutsideWorkingHoursSchema. */
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema,
  /**
   * APPOINTMENT and TASK only: the four lifecycle instants, for a calendar moving a block that is
   * already drawn from its Ist-Zeiten. The Ist-Minuten are derived from these on the server — a
   * drag therefore sends all four, so the re-derived durations equal what was dragged.
   * `undefined` leaves one alone, `null` clears it.
   */
  travelStartedAt: z20.string().datetime().nullable().optional(),
  actualWorkStartsAt: z20.string().datetime().nullable().optional(),
  actualEndsAt: z20.string().datetime().nullable().optional(),
  departureEndedAt: z20.string().datetime().nullable().optional()
});
var UpsertScheduleBreakDtoSchema = z20.object({
  technicianId: z20.coerce.number().int().positive(),
  startsAt: z20.string().datetime(),
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema
});

// src/schedule/schedule-read.ts
import { z as z21 } from "zod";
var ScheduleQuerySchema = z21.object({
  from: z21.string().datetime(),
  to: z21.string().datetime(),
  /**
   * Omitted means *every* technician — that is the all-workers calendar, and it is served by a
   * dedicated `@@index([starts_at, ends_at])`. A single value and a repeated one both normalise
   * to an array, because Express hands over `string` for `?technicianIds=1` and `string[]` for
   * `?technicianIds=1&technicianIds=2`.
   */
  technicianIds: z21.union([
    z21.coerce.number().int().positive(),
    z21.array(z21.coerce.number().int().positive()).max(200)
  ]).optional().transform((v) => v == null ? void 0 : Array.isArray(v) ? v : [v]),
  types: z21.union([ScheduleEntryTypeSchema, z21.array(ScheduleEntryTypeSchema)]).optional().transform((v) => v == null ? void 0 : Array.isArray(v) ? v : [v]),
  /**
   * Join each appointment's work order from Immotion (address, tenant, attachment count).
   *
   * Deliberately NOT `z.coerce.boolean()`: that coerces the *string* `"false"` to `true`, so an
   * explicit opt-out would silently do the opposite of what it says.
   */
  includeWorkOrder: z21.enum(["true", "false"]).optional().default("true").transform((v) => v === "true")
});
var ScheduleTechnicianSchema = z21.object({
  technicianId: z21.number().int(),
  technicianName: z21.string().nullable(),
  technicianAvatar: z21.string().nullable(),
  workingDays: z21.array(z21.number().int().min(1).max(7)),
  shiftStart: z21.string().datetime().nullable(),
  shiftEnd: z21.string().datetime().nullable(),
  defaultBreakStart: z21.string().datetime().nullable(),
  breakDurationMinutes: z21.number().int().min(0),
  /**
   * Gewerk ids the technician covers (`TechnicianProfile.trades`) — the same id space as
   * `WorkOrder.external.gewerkId`. The disposition board matches on it to list fitting technicians
   * first when planning a Termin. Empty for a technician without a profile.
   */
  trades: z21.array(z21.number().int()),
  /**
   * The Leitung greyed this technician out of the default calendar (`TechnicianProfile.calendarHidden`).
   * The list arrives already sorted by the saved calendar order with hidden technicians last, so the
   * flag only drives how a row looks. `false` for a technician without a profile.
   */
  calendarHidden: z21.boolean(),
  /**
   * Everything occupying this technician's time in the window, pre-sorted by
   * `startsAt → endsAt → type → id`. Materialized rows and projected default breaks are merged
   * into this one list; a projected break is the entry with `id: null`.
   */
  entries: z21.array(ScheduleEntrySchema)
});
var ScheduleResultSchema = z21.object({
  from: z21.string().datetime(),
  to: z21.string().datetime(),
  technicians: z21.array(ScheduleTechnicianSchema),
  appointments: z21.array(AppointmentSchema),
  tasks: z21.array(TaskListItemSchema),
  absences: z21.array(AbsenceSchema)
});

// src/reschedule/reschedule.ts
import { z as z22 } from "zod";
var RescheduleItemTypeSchema = z22.enum(["APPOINTMENT", "TASK"]);
var RescheduleItemKindSchema = z22.enum(["RESCHEDULE", "FOLLOW_UP", "MATERIAL_ORDERED"]);
var RescheduleReasonFilterSchema = z22.enum([
  ...AbsenceCategorySchema.options,
  "NOT_CLOSED"
]);
var RescheduleItemSchema = z22.object({
  id: z22.number().int(),
  type: RescheduleItemTypeSchema,
  kind: RescheduleItemKindSchema,
  title: z22.string(),
  scheduledDate: z22.string().datetime(),
  /** Why it is listed here. `reschedulingReason` is only filled in for cause `ABSENCE`. */
  reschedulingCause: RescheduleCauseSchema.nullable().optional(),
  reschedulingReason: z22.string().nullable().optional(),
  reschedulingRequestedAt: z22.string().nullable().optional(),
  /** Anfahrt or Arbeit was tapped. Splits cause `NOT_CLOSED` into „Nicht gestartet" / „Nicht abgeschlossen". */
  workStarted: z22.boolean(),
  /** When a `MATERIAL_ORDERED` item becomes the office's business (sign-off + 14 days). */
  materialDueAt: z22.string().datetime().nullable().optional(),
  /** `Appointment.followUpReason`, for kinds `FOLLOW_UP` and `MATERIAL_ORDERED`. */
  followUpReason: z22.string().nullable().optional(),
  technicianName: z22.string().nullable().optional(),
  entityMeta: z22.union([
    z22.object({ workOrderId: z22.string() }),
    z22.object({ taskType: z22.string() }),
    z22.object({ category: z22.string() })
  ])
});
var RescheduleFiltersSchema = z22.object({
  page: z22.coerce.number().int().positive().optional(),
  limit: z22.coerce.number().int().positive().max(500).optional(),
  type: RescheduleItemTypeSchema.optional(),
  kind: RescheduleItemKindSchema.optional(),
  reason: RescheduleReasonFilterSchema.optional(),
  technicianName: z22.string().optional(),
  dateFrom: z22.string().optional(),
  dateTo: z22.string().optional(),
  title: z22.string().optional(),
  sortField: z22.enum(["scheduledDate", "type", "technicianName"]).optional(),
  sortDirection: z22.enum(["ASC", "DESC"]).optional()
});
var RescheduleKindCountsSchema = z22.object({
  all: z22.number().int(),
  reschedule: z22.number().int(),
  followUp: z22.number().int(),
  materialOrdered: z22.number().int(),
  /** Of `materialOrdered`, those whose 14 days are over. */
  materialOrderedDue: z22.number().int()
});
var RescheduleCountResponseSchema = z22.object({
  count: z22.number().int()
});

// src/reschedule/material-ordered.ts
var MATERIAL_ORDERED_REASON = "Material bestellt";
var LEGACY_MATERIAL_REASON = "Material Bestellung";
var MATERIAL_ORDERED_DUE_DAYS = 14;
var isMaterialOrderedReason = (reason) => !!reason && (reason.startsWith(MATERIAL_ORDERED_REASON) || reason.startsWith(LEGACY_MATERIAL_REASON));

// src/controlling/controlling.ts
import * as zod7 from "zod";
var z24 = zod7.z ?? zod7;
var ControllingFollowUpReasonCodeSchema = z24.enum([
  "MIETER_NICHT_VOR_ORT",
  "TERMIN_NICHT_EINGEHALTEN",
  "WEITERER_GRUND",
  "MIT_ANDEREM_GEWERK",
  "MATERIAL_BESTELLUNG"
]);
var ControllingReasonFilterValueSchema = z24.union([
  ControllingFollowUpReasonCodeSchema,
  AbsenceCategorySchema
]);
var ControllingSortFieldSchema = z24.enum([
  "repairDate",
  "workOrderId",
  "technician",
  "tenant",
  "ve",
  "building",
  "trade",
  "gewerkId",
  "actualWorkHours",
  "plannedWorkHours",
  "actualTravelHours",
  "plannedTravelHours",
  "actualTotalHours",
  "deviation",
  "deviationHours",
  "wertEuro"
]);
var ScheduleStatusSchema = z24.enum([
  "WITHOUT_FOLLOW_UP",
  "WITH_FOLLOW_UPS",
  "RESCHEDULED_BY_OFFICE",
  "RESCHEDULED_BY_TECHNICIAN"
]);
var DeviationStatusSchema = z24.enum([
  "ON_SCHEDULE",
  "OVER_TARGET",
  "UNDER_TARGET"
]);
var ControllingFiltersSchema = z24.object({
  page: z24.coerce.number().int().positive().optional(),
  limit: z24.coerce.number().int().positive().max(100).optional(),
  sortField: ControllingSortFieldSchema.optional(),
  sortDirection: z24.enum(["ASC", "DESC"]).optional(),
  dateFrom: z24.string().optional(),
  dateTo: z24.string().optional(),
  workOrderId: z24.string().optional(),
  technicianId: z24.coerce.number().int().optional(),
  tenant: z24.string().optional(),
  ve: z24.string().optional(),
  building: z24.string().optional(),
  tradeId: z24.coerce.number().int().optional(),
  technicianTradeId: z24.coerce.number().int().optional(),
  scheduleStatus: z24.array(ScheduleStatusSchema).optional(),
  deviationStatus: z24.array(DeviationStatusSchema).optional(),
  actualTravelHoursMin: z24.coerce.number().optional(),
  actualTravelHoursMax: z24.coerce.number().optional(),
  actualWorkHoursMin: z24.coerce.number().optional(),
  actualWorkHoursMax: z24.coerce.number().optional(),
  actualTotalHoursMin: z24.coerce.number().optional(),
  actualTotalHoursMax: z24.coerce.number().optional(),
  deviationMin: z24.coerce.number().optional(),
  deviationMax: z24.coerce.number().optional(),
  reasons: z24.array(ControllingReasonFilterValueSchema).optional()
});
var ControllingAggregatesSchema = z24.object({
  totalPlannedWorkHours: z24.number(),
  totalActualWorkHours: z24.number(),
  totalPlannedTravelHours: z24.number(),
  totalActualTravelHours: z24.number(),
  totalPlannedHours: z24.number(),
  totalActualHours: z24.number(),
  deviation: z24.number(),
  totalWertEuro: z24.number()
});
var ControllingRowSchema = z24.object({
  workOrderId: z24.string(),
  tenant: z24.string(),
  technicians: z24.array(z24.string()),
  ve: z24.string(),
  building: z24.string(),
  trade: z24.string(),
  scheduleStatus: ScheduleStatusSchema,
  deviationStatus: DeviationStatusSchema,
  actualWorkHours: z24.number(),
  plannedWorkHours: z24.number(),
  actualTravelHours: z24.number(),
  plannedTravelHours: z24.number(),
  actualTotalHours: z24.number(),
  plannedTotalHours: z24.number(),
  repairDateFrom: z24.string(),
  repairDateTo: z24.string(),
  appointmentCount: z24.number(),
  deviation: z24.number(),
  reasons: z24.array(z24.string()),
  technicianTradeIds: z24.array(z24.number().int()),
  gewerkId: z24.number().int().nullable(),
  deviationHours: z24.number(),
  wertEuro: z24.number().nullable(),
  preis: z24.number().nullable()
});
var ControllingPdfColumnSchema = z24.enum([
  "workOrderId",
  "scheduleStatus",
  "repairDate",
  "building",
  "tenant",
  "gewerkId",
  "trade",
  "ve",
  "technician",
  "plannedWorkHours",
  "actualWorkHours",
  "plannedTravelHours",
  "actualTravelHours",
  "plannedTotalHours",
  "actualTotalHours",
  "deviation",
  "deviationHours",
  "reasons",
  "wertEuro"
]);
var ControllingActiveFilterSchema = z24.object({
  label: z24.string(),
  value: z24.string()
});
var ControllingPdfRequestBodySchema = z24.object({
  dateFrom: z24.string().optional(),
  dateTo: z24.string().optional(),
  workOrderId: z24.string().optional(),
  technicianId: z24.coerce.number().int().optional(),
  tenant: z24.string().optional(),
  ve: z24.string().optional(),
  building: z24.string().optional(),
  tradeId: z24.coerce.number().int().optional(),
  technicianTradeId: z24.coerce.number().int().optional(),
  scheduleStatus: z24.array(ScheduleStatusSchema).optional(),
  deviationStatus: z24.array(DeviationStatusSchema).optional(),
  actualTravelHoursMin: z24.coerce.number().optional(),
  actualTravelHoursMax: z24.coerce.number().optional(),
  actualWorkHoursMin: z24.coerce.number().optional(),
  actualWorkHoursMax: z24.coerce.number().optional(),
  actualTotalHoursMin: z24.coerce.number().optional(),
  actualTotalHoursMax: z24.coerce.number().optional(),
  deviationMin: z24.coerce.number().optional(),
  deviationMax: z24.coerce.number().optional(),
  reasons: z24.array(ControllingReasonFilterValueSchema).optional(),
  sortField: ControllingSortFieldSchema.optional(),
  sortDirection: z24.enum(["ASC", "DESC"]).optional(),
  selectedColumns: z24.array(ControllingPdfColumnSchema).min(1),
  activeFilters: z24.array(ControllingActiveFilterSchema)
});
var ControllingXlsxRequestBodySchema = ControllingPdfRequestBodySchema.omit({
  activeFilters: true
});

// src/dashboard/dashboard.ts
import * as zod8 from "zod";
var z26 = zod8.z ?? zod8;
var IsoDateSchema = z26.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var DashboardStatusCategorySchema = z26.enum([
  "NOT_PLANNED",
  "PLANNED",
  "FOLLOW_UP_REQUIRED",
  "NOT_REACHED",
  "COMPLETED"
]);
var DashboardFiltersSchema = z26.object({
  dateFrom: IsoDateSchema,
  dateTo: IsoDateSchema,
  technicianId: z26.coerce.number().int().optional()
});
var DashboardCellSchema = z26.object({
  month: z26.string().regex(/^\d{4}-\d{2}$/),
  gewerkId: z26.number().int().nullable(),
  gewerkName: z26.string().nullable(),
  category: DashboardStatusCategorySchema,
  orderCount: z26.number().int(),
  plannedMinutes: z26.number().int(),
  actualMinutes: z26.number().int(),
  sollEuro: z26.number(),
  istEuro: z26.number()
});
var DashboardAggregatesSchema = z26.object({
  cells: z26.array(DashboardCellSchema),
  // false when Immotion could not be reached: Gewerk is missing and the euro values are 0.
  pricingAvailable: z26.boolean()
});

// src/mail-setting/mail-setting.ts
import * as zod9 from "zod";
var z28 = zod9.z ?? zod9;
var MailSettingSchema = z28.object({
  host: z28.string().min(1),
  port: z28.coerce.number().int().min(1).max(65535),
  user: z28.string(),
  from: z28.string().email()
});
var MailSettingResponseSchema = MailSettingSchema.extend({
  isPassSet: z28.boolean(),
  werkhofleiterEmails: z28.array(z28.string().email()).default([])
});
var UpdateMailSettingDtoSchema = z28.object({
  host: z28.string().min(1).optional(),
  port: z28.coerce.number().int().min(1).max(65535).optional(),
  user: z28.string().optional(),
  pass: z28.string().trim().min(1).optional(),
  from: z28.string().email().optional(),
  werkhofleiterEmails: z28.array(z28.string().email()).optional()
});

// src/arbeitszeit-report/arbeitszeit-report.ts
import * as zod10 from "zod";
var z30 = zod10.z ?? zod10;
var ArbeitszeitGesamtCategorySchema = z30.enum([
  "auftraege",
  "allgemeinzeit",
  "urlaub",
  "krankheit",
  "privat",
  "gesamt"
]);
var ArbeitszeitGesamtRowSchema = z30.object({
  category: ArbeitszeitGesamtCategorySchema,
  sollStd: z30.number().nullable(),
  istStd: z30.number(),
  abweichungStd: z30.number().nullable(),
  abweichungPct: z30.number().nullable(),
  wertEuro: z30.number().nullable()
});
var AuslastungRowSchema = z30.object({
  technicianId: z30.number().int(),
  technicianName: z30.string(),
  sollStd: z30.number(),
  reparaturzeitenStd: z30.number(),
  nichtReparaturzeitenStd: z30.number(),
  istStd: z30.number(),
  abwesenheitenStd: z30.number(),
  auslastungPct: z30.number().nullable()
});
var ArbeitsorgOrganisationTypeSchema = z30.enum([
  "ERRANDS",
  "GENERAL_TASK",
  "SICK_LEAVE",
  "VACATION",
  "PERSONAL_LEAVE",
  "TRAINING"
]);
var ArbeitsorgRowSchema = z30.object({
  id: z30.string(),
  technicianId: z30.number().int().nullable(),
  technicianName: z30.string(),
  organisationType: ArbeitsorgOrganisationTypeSchema,
  bereich: z30.string().nullable(),
  dateFrom: z30.string(),
  dateTo: z30.string(),
  timeFrom: z30.string().nullable(),
  timeTo: z30.string().nullable(),
  istStd: z30.number().nullable(),
  wertEuro: z30.number().nullable()
});
var PaginatedMetaSchema = z30.object({
  page: z30.number().int(),
  limit: z30.number().int(),
  total: z30.number().int()
});
var ArbeitszeitReportFiltersSchema = z30.object({
  dateFrom: z30.string().optional(),
  dateTo: z30.string().optional(),
  technicianId: z30.coerce.number().int().optional(),
  tradeId: z30.coerce.number().int().optional(),
  scheduleStatus: z30.array(ScheduleStatusSchema).optional(),
  deviationStatus: z30.array(DeviationStatusSchema).optional(),
  pageAuftraege: z30.coerce.number().int().positive().optional(),
  pageAuslastung: z30.coerce.number().int().positive().optional(),
  pageArbeitsorganisation: z30.coerce.number().int().positive().optional()
});
var ArbeitszeitReportResponseSchema = z30.object({
  gesamtuebersicht: z30.array(ArbeitszeitGesamtRowSchema),
  auslastung: z30.object({
    items: z30.array(AuslastungRowSchema),
    meta: PaginatedMetaSchema
  }),
  auftraege: z30.object({
    items: z30.array(ControllingRowSchema),
    meta: PaginatedMetaSchema,
    aggregates: ControllingAggregatesSchema
  }),
  arbeitsorganisation: z30.object({
    items: z30.array(ArbeitsorgRowSchema),
    meta: PaginatedMetaSchema
  })
});

// src/push-subscription/push-subscription.ts
import { z as z31 } from "zod";
var PushSubscriptionKeysSchema = z31.object({
  /** Client public key. Payloads are encrypted to it (RFC 8291), so the push service relays ciphertext. */
  p256dh: z31.string().min(1, "p256dh fehlt"),
  /** 16-byte shared secret that feeds the same encryption. */
  auth: z31.string().min(1, "auth fehlt")
});
var PushEndpointSchema = z31.string().regex(/^https:\/\/\S+$/, "Ung\xFCltiger Push-Endpunkt").max(2048);
var SubscribePushDtoSchema = z31.object({
  endpoint: PushEndpointSchema,
  keys: PushSubscriptionKeysSchema,
  userAgent: z31.string().max(512).optional()
});
var UnsubscribePushDtoSchema = z31.object({
  endpoint: PushEndpointSchema
});
var PushDeviceSchema = z31.object({
  id: z31.number().int(),
  userAgent: z31.string().nullable(),
  createdAt: z31.string().datetime(),
  lastSeenAt: z31.string().datetime()
});
var VapidPublicKeyResponseSchema = z31.object({
  publicKey: z31.string().min(1)
});
var SendTestPushResponseSchema = z31.object({
  sent: z31.number().int().nonnegative(),
  failed: z31.number().int().nonnegative(),
  removed: z31.number().int().nonnegative()
});

// src/push-message/push-message.ts
import { z as z32 } from "zod";
var PUSH_TARGET_PATH_PATTERN = /^\/(?![/\\])[^\s\\]*$/;
var PUSH_TARGET_PATH_MESSAGE = "Pfad muss mit einem einzelnen / beginnen";
var SendPushMessageDtoSchema = z32.object({
  userIds: z32.array(z32.number().int().positive()).min(1, "Empf\xE4nger fehlen").max(500),
  title: z32.string().min(1, "Titel fehlt").max(80),
  body: z32.string().min(1, "Text fehlt").max(400),
  /** App-relative on purpose — see PUSH_TARGET_PATH_PATTERN for what that costs to enforce. */
  url: z32.string().regex(PUSH_TARGET_PATH_PATTERN, PUSH_TARGET_PATH_MESSAGE).max(512).optional()
});
var PushStatusFilterSchema = z32.enum(["with", "without", "all"]);
var PushAdminSortFieldSchema = z32.enum(["name", "devices"]);
var PushAdminUsersFiltersSchema = z32.preprocess(
  (raw) => {
    if (raw !== null && typeof raw === "object" && !Array.isArray(raw)) {
      const { role, roles, ...rest } = raw;
      const normalizedRoles = roles !== void 0 ? Array.isArray(roles) ? roles : [roles] : role !== void 0 ? [role] : void 0;
      return normalizedRoles !== void 0 ? { ...rest, roles: normalizedRoles } : rest;
    }
    return raw;
  },
  z32.object({
    page: z32.coerce.number().int().positive().optional(),
    limit: z32.coerce.number().int().positive().max(500).optional(),
    search: z32.string().optional(),
    roles: z32.array(UserRoleSchema).optional(),
    pushStatus: PushStatusFilterSchema.optional(),
    sortField: PushAdminSortFieldSchema.optional(),
    sortOrder: z32.enum(["ASC", "DESC"]).optional()
  })
);
var PushAdminUserSchema = z32.object({
  id: z32.number().int(),
  name: z32.string(),
  email: z32.string(),
  roles: z32.array(UserRoleSchema),
  devices: z32.array(PushDeviceSchema)
});
var PaginatedMetaSchema2 = z32.object({
  page: z32.number().int(),
  limit: z32.number().int(),
  total: z32.number().int()
});
var PushAdminUsersResponseSchema = z32.object({
  items: z32.array(PushAdminUserSchema),
  meta: PaginatedMetaSchema2
});
var PushAdminStatsSchema = z32.object({
  usersTotal: z32.number().int().nonnegative(),
  usersWithPush: z32.number().int().nonnegative(),
  devicesTotal: z32.number().int().nonnegative()
});
var PushMessageSchema = z32.object({
  id: z32.number().int(),
  title: z32.string(),
  body: z32.string(),
  url: z32.string().nullable(),
  createdAt: z32.string().datetime(),
  /** Null when the author's account was deleted since. */
  createdBy: z32.object({ id: z32.number().int(), name: z32.string() }).nullable(),
  /** Who was addressed, by name — the Verlauf's answer to "an wen ging das?". */
  recipients: z32.array(z32.object({ id: z32.number().int(), name: z32.string() })),
  recipientCount: z32.number().int().nonnegative(),
  sentCount: z32.number().int().nonnegative(),
  failedCount: z32.number().int().nonnegative(),
  removedCount: z32.number().int().nonnegative()
});
var PushMessageListFiltersSchema = z32.object({
  page: z32.coerce.number().int().positive().optional(),
  limit: z32.coerce.number().int().positive().max(500).optional()
});
var PushMessageListResponseSchema = z32.object({
  items: z32.array(PushMessageSchema),
  meta: PaginatedMetaSchema2
});
var SendPushMessageResponseSchema = PushMessageSchema.extend({
  /** Requested ids that were archived or unknown — shown to the admin, never swallowed. */
  skippedUserIds: z32.array(z32.number().int())
});
export {
  AbsenceCategorySchema,
  AbsenceFiltersSchema,
  AbsenceListResultSchema,
  AbsenceScheduleDetailsSchema,
  AbsenceSchema,
  AbsenceSortDirectionSchema,
  AbsenceSortFieldSchema,
  AbsenceTechnicianSchema,
  AllowOutsideWorkingHoursSchema,
  AppointmentScheduleDetailsSchema,
  AppointmentSchema,
  AppointmentSeriesSummarySchema,
  AppointmentSignOffResultSchema,
  AppointmentStatusSchema,
  AppointmentTechnicianSchema,
  ArbeitsorgOrganisationTypeSchema,
  ArbeitsorgRowSchema,
  ArbeitszeitGesamtCategorySchema,
  ArbeitszeitGesamtRowSchema,
  ArbeitszeitReportFiltersSchema,
  ArbeitszeitReportResponseSchema,
  AuditLogActionSchema,
  AuditLogActorSchema,
  AuditLogEntityTypeSchema,
  AuditLogFiltersSchema,
  AuditLogSchema,
  AuslastungRowSchema,
  AuthErrorCode,
  BreakScheduleDetailsSchema,
  CLOSED_WORK_STATUSES,
  CompleteAppointmentDtoSchema,
  CompleteTaskDtoSchema,
  CompleteWorkItemDtoSchema,
  ContactAttemptSchema,
  ControllingActiveFilterSchema,
  ControllingAggregatesSchema,
  ControllingFiltersSchema,
  ControllingFollowUpReasonCodeSchema,
  ControllingPdfColumnSchema,
  ControllingPdfRequestBodySchema,
  ControllingReasonFilterValueSchema,
  ControllingRowSchema,
  ControllingSortFieldSchema,
  ControllingXlsxRequestBodySchema,
  CreateAbsenceDtoSchema,
  CreateAbsenceEntryDtoSchema,
  CreateAppointmentDtoSchema,
  CreateAppointmentEntryDtoSchema,
  CreateAppointmentStatusSchema,
  CreateBreakEntryDtoSchema,
  CreateContactAttemptDtoSchema,
  CreateScheduleEntryDtoSchema,
  CreateTaskDtoSchema,
  CreateTaskEntryDtoSchema,
  CreateTechnicianProfileDtoSchema,
  CreateUserDtoSchema,
  CreateWorkOrderDtoSchema,
  CurrentWorkSessionResponseSchema,
  CurrentWorkSessionSchema,
  DashboardAggregatesSchema,
  DashboardCellSchema,
  DashboardFiltersSchema,
  DashboardStatusCategorySchema,
  DeleteAppointmentQuerySchema,
  DeleteAppointmentResultSchema,
  DeleteTaskQuerySchema,
  DeleteTaskResultSchema,
  DeviationStatusSchema,
  EndDepartureDtoSchema,
  FollowUpAppointmentDtoSchema,
  GewerkFiltersSchema,
  GewerkSchema,
  ImmotionAttachmentSchema,
  MATERIAL_ORDERED_DUE_DAYS,
  MATERIAL_ORDERED_REASON,
  MAX_CONTACT_ATTEMPTS,
  MailSettingResponseSchema,
  MailSettingSchema,
  NOT_REACHED_REMINDER_DAYS,
  PUSH_TARGET_PATH_MESSAGE,
  PUSH_TARGET_PATH_PATTERN,
  PaginatedMetaSchema,
  PushAdminSortFieldSchema,
  PushAdminStatsSchema,
  PushAdminUserSchema,
  PushAdminUsersFiltersSchema,
  PushAdminUsersResponseSchema,
  PushDeviceSchema,
  PushMessageListFiltersSchema,
  PushMessageListResponseSchema,
  PushMessageSchema,
  PushStatusFilterSchema,
  PushSubscriptionKeysSchema,
  ROLE_RANK,
  RecurrenceFrequencySchema,
  RecurrenceInputSchema,
  RescheduleCauseSchema,
  RescheduleCountResponseSchema,
  RescheduleFiltersSchema,
  RescheduleItemKindSchema,
  RescheduleItemSchema,
  RescheduleItemTypeSchema,
  RescheduleKindCountsSchema,
  RescheduleReasonFilterSchema,
  Role,
  ScheduleConflictItemSchema,
  ScheduleEntrySchema,
  ScheduleEntryTechnicianSchema,
  ScheduleEntryTypeSchema,
  ScheduleQuerySchema,
  ScheduleResultSchema,
  ScheduleSeriesSchema,
  ScheduleSeriesTypeSchema,
  ScheduleStatusSchema,
  ScheduleTaskDtoSchema,
  ScheduleTechnicianSchema,
  ScheduleWorkingHoursViolationReasonSchema,
  ScheduleWorkingHoursViolationSchema,
  SegmentedSlotSchema,
  SendPushMessageDtoSchema,
  SendPushMessageResponseSchema,
  SendTestPushResponseSchema,
  SlotSchema,
  SpanSlotSchema,
  StartWorkItemDtoSchema,
  SubscribePushDtoSchema,
  TaskFiltersSchema,
  TaskListItemSchema,
  TaskScheduleDetailsSchema,
  TaskSchema,
  TaskSeriesSummarySchema,
  TaskSortDirectionSchema,
  TaskSortFieldSchema,
  TaskStatusSchema,
  TaskTechnicianSchema,
  TaskTypeSchema,
  TechnicianAttachmentSchema,
  TechnicianProfileDataSchema,
  TechnicianProfileSchema,
  TechnicianTasksQuerySchema,
  TechnicianTasksResponseSchema,
  UnsubscribePushDtoSchema,
  UpdateAbsenceDtoSchema,
  UpdateAppointmentDtoSchema,
  UpdateAppointmentSeriesResultSchema,
  UpdateContactCommentDtoSchema,
  UpdateMailSettingDtoSchema,
  UpdateScheduleEntryDtoSchema,
  UpdateScheduleSeriesDtoSchema,
  UpdateTaskDtoSchema,
  UpdateTaskSeriesResultSchema,
  UpdateTechnicianCalendarOrderDtoSchema,
  UpdateTechnicianCommentDtoSchema,
  UpdateUserDtoSchema,
  UpdateWorkOrderStatusDtoSchema,
  UpsertScheduleBreakDtoSchema,
  UserFiltersSchema,
  UserRoleSchema,
  UserRolesSchema,
  UserStatusFilterSchema,
  VapidPublicKeyResponseSchema,
  WORK_PHASE_RUNNING_LABELS,
  WorkOrderExternalSnapshotSchema,
  WorkOrderFiltersSchema,
  WorkOrderLocalDataSchema,
  WorkOrderMieterSchema,
  WorkOrderSchema,
  WorkOrderSortDirectionSchema,
  WorkOrderSortFieldSchema,
  WorkOrderStatusSchema,
  WorkOrderWithAppointmentsSchema,
  WorkPhaseSchema,
  WorkSessionItemTypeSchema,
  WorkTimeFieldSchema,
  deriveWorkPhase,
  deriveWorkState,
  hasAnyRole,
  hasOnlyRole,
  hasRole,
  highestRole,
  isMaterialOrderedReason,
  isSessionOpen
};
